/**
 * Money helpers. Amounts are pounds in the UI and the API, but any arithmetic
 * (line totals, VAT, sums) should happen in integer pence to avoid
 * floating-point drift (0.1 + 0.2 ≠ 0.3).
 */

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MINUS = '−';

/** Parse a number or a display string ("£1,850.50", "-500", "1 200") to a Number, or NaN. */
export function parseAmount(v) {
  if (typeof v === 'number') return v;
  if (v == null) return NaN;
  const s = String(v).trim().replace(/[£,\s]/g, '').replace(MINUS, '-');
  if (s === '' || s === '-') return NaN;
  return Number(s);
}

/**
 * Pounds → "£1,850.50" / "−£500.00" (true minus sign). Always 2dp, always GBP.
 * Empty / null → `blank` ("—" by default). Non-numeric strings pass through.
 */
export function formatGBP(v, { blank = '—' } = {}) {
  if (v == null || v === '' || v === '—') return blank;
  const n = parseAmount(v);
  if (!Number.isFinite(n)) return String(v);
  const out = gbp.format(Math.abs(n) < 0.005 ? 0 : n);
  return out.startsWith('-') ? `${MINUS}${out.slice(1)}` : out;
}

/** Pounds (number or string) → integer pence. NaN input → 0. */
export function toPence(v) {
  const n = parseAmount(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** Integer pence → pounds as a Number (e.g. 185050 → 1850.5). */
export function fromPence(p) {
  const n = Number(p);
  return Number.isFinite(n) ? Math.round(n) / 100 : 0;
}

/**
 * Sum pound amounts exactly. Returns integer PENCE — wrap with fromPence() for pounds.
 *   sumPence([0.1, 0.2])          → 30
 *   sumPence(lines, (l) => l.total)
 */
export function sumPence(values = [], pick = (x) => x) {
  return (values || []).reduce((acc, v) => acc + toPence(pick(v)), 0);
}

export default formatGBP;
